import _extends from "/usr/app/node_modules/@babel/runtime/helpers/esm/extends.js";
import _objectWithoutProperties from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["options", "onSearch", "onSelect", "searchFunction", "placeholder", "limit", "debounceDelay", "renderItem", "getItemKey", "showResults", "id", "type", "inputMode", "className", "searchTerm", "onSearchTermChange"];
import React from "react";
var __jsx = React.createElement;
import { useState, useMemo, useEffect } from "react";
import useDebounce from "@hooks/useDebounce";
import { Icon } from "@syarahtech/ui";
import { useTranslation } from "@i18n";
import { twMerge } from "tailwind-merge";
/**
 * FlowSearchBar - Optimized search component with debouncing
 *
 * Features:
 * - Debounced search to prevent performance issues
 * - Searches in both Arabic and English (using seo_url field)
 * - Memory efficient - only uses already loaded data
 * - Configurable search function for flexibility
 *
 * @example
 * <FlowSearchBar
 *   options={makes}
 *   searchFunction={(items, term) => searchItems(items, term, 20)}
 *   onSearch={(results) => setSearchResults(results)}
 *   onSelect={(make) => handleMakeSelect(make)}
 *   getItemKey={(item) => item.id}
 *   placeholder="Search brands..."
 * />
 */
function FlowSearchBar(_ref) {
  var options = _ref.options,
    onSearch = _ref.onSearch,
    onSelect = _ref.onSelect,
    searchFunction = _ref.searchFunction,
    placeholder = _ref.placeholder,
    _ref$limit = _ref.limit,
    limit = _ref$limit === void 0 ? 20 : _ref$limit,
    _ref$debounceDelay = _ref.debounceDelay,
    debounceDelay = _ref$debounceDelay === void 0 ? 300 : _ref$debounceDelay,
    renderItem = _ref.renderItem,
    getItemKey = _ref.getItemKey,
    _ref$showResults = _ref.showResults,
    showResults = _ref$showResults === void 0 ? true : _ref$showResults,
    id = _ref.id,
    _ref$type = _ref.type,
    type = _ref$type === void 0 ? "text" : _ref$type,
    _ref$inputMode = _ref.inputMode,
    inputMode = _ref$inputMode === void 0 ? "text" : _ref$inputMode,
    className = _ref.className,
    controlledSearchTerm = _ref.searchTerm,
    onSearchTermChange = _ref.onSearchTermChange,
    props = _objectWithoutProperties(_ref, _excluded);
  var _useTranslation = useTranslation(),
    tShort = _useTranslation.tShort;
  var _useState = useState(""),
    internalSearchTerm = _useState[0],
    setInternalSearchTerm = _useState[1];
  var _useState2 = useState(false),
    isFocused = _useState2[0],
    setIsFocused = _useState2[1];

  // Use controlled or internal state
  var isControlled = controlledSearchTerm !== undefined;
  var searchTerm = isControlled ? controlledSearchTerm : internalSearchTerm;
  var setSearchTerm = function setSearchTerm(term) {
    if (isControlled && onSearchTermChange) {
      onSearchTermChange(term);
    } else {
      setInternalSearchTerm(term);
    }
  };

  // Debounce the search term to avoid excessive re-renders
  var debouncedSearchTerm = useDebounce(searchTerm, debounceDelay);

  // Memoize search results to prevent recalculation
  var searchResults = useMemo(function () {
    if (!debouncedSearchTerm) {
      // Return limited options when no search term
      return options.slice(0, limit);
    }
    return searchFunction(options, debouncedSearchTerm, limit);
  }, [debouncedSearchTerm, options, searchFunction, limit]);

  // Call onSearch when results change or on initial mount
  useEffect(function () {
    onSearch(searchResults);
  }, [debouncedSearchTerm, options.length]);
  var handleInputChange = function handleInputChange(e) {
    setSearchTerm(e.target.value);
  };
  var handleClear = function handleClear() {
    setSearchTerm("");
    onSearch(options.slice(0, limit));
  };
  var handleItemClick = function handleItemClick(item) {
    if (onSelect) {
      onSelect(item);
      setSearchTerm("");
      setIsFocused(false);
    }
  };
  var showDropdown = showResults && isFocused && searchResults.length > 0;
  return /*#__PURE__*/React.createElement("div", {
    className: twMerge("relative w-full border border-gray-echo border-solid rounded-lg overflow-hidden", className)
  }, /*#__PURE__*/React.createElement("div", {
    className: "relative"
  }, /*#__PURE__*/React.createElement(Icon, {
    icon: "mingcute--search-line",
    className: "absolute en:left-3 right-3 top-1/2 transform -translate-y-1/2 text-primary-new",
    width: 6,
    height: 6
  }), /*#__PURE__*/React.createElement("input", _extends({
    id: id,
    type: type,
    autoComplete: "off",
    value: searchTerm,
    onChange: handleInputChange,
    autoFocus: false,
    onFocus: function onFocus() {
      return setIsFocused(true);
    },
    onBlur: function onBlur() {
      // Delay to allow click events on dropdown items
      setTimeout(function () {
        return setIsFocused(false);
      }, 200);
    },
    placeholder: placeholder || tShort("sell_trade_v2/search_placeholder", {
      ns: "web_sell_trade",
      defaultValue: "Search..."
    }),
    className: "w-full pl-10 pr-10 py-3 outline-0 border-none focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-transparent placeholder-shown:text-[#D6D6D6] placeholder-shown:font-regular placeholder-shown:text-base text-base placeholder:text-[#d6d6d6] placeholder:text-sm"
  }, props)), searchTerm && /*#__PURE__*/React.createElement("button", {
    onClick: handleClear,
    className: "absolute en:right-3 en:left-[unset] left-3 top-1/2 transform -translate-y-1/2 text-gray-text bg-transparent outline-none border-none flex",
    type: "button"
  }, /*#__PURE__*/React.createElement(Icon, {
    icon: "material-symbols--close",
    width: 5,
    height: 5
  }))), showDropdown && /*#__PURE__*/React.createElement("div", {
    className: "absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
  }, searchResults.length === 0 ? /*#__PURE__*/React.createElement("div", {
    className: "p-4 text-center text-gray-500"
  }, tShort("sell_trade_v2/no_results", {
    ns: "web_sell_trade",
    defaultValue: "No results found"
  })) : /*#__PURE__*/React.createElement("div", {
    className: "py-2"
  }, searchResults.map(function (item) {
    return /*#__PURE__*/React.createElement("button", {
      key: getItemKey(item),
      onClick: function onClick() {
        return handleItemClick(item);
      },
      className: "w-full px-4 py-3 text-right hover:bg-gray-50 transition-colors duration-150 border-b border-gray-100 last:border-b-0",
      type: "button"
    }, renderItem ? renderItem(item) : String(item));
  }))));
}
export default FlowSearchBar;